/**
 * Pickup Time Calculator
 * Calculates when a product can be picked up based on store opening hours
 */

(function () {
    'use strict';

    // Swedish weekday names (indexed by JS getDay(): 0=Sunday, 1=Monday, etc.)
    const WEEKDAY_NAMES = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'];

    // Swedish weekday names for matching against metafield data (starts with Monday)
    const WEEKDAY_NAMES_SV = ['måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag', 'söndag'];

    /**
     * Convert JS dayOfWeek (0=Sunday) to metafield array index (0=Monday)
     */
    function jsDayToArrayIndex(jsDayOfWeek) {
        return jsDayOfWeek === 0 ? 6 : jsDayOfWeek - 1;
    }

    // Minimum minutes before closing to still allow same-day pickup
    const MIN_MINUTES_BEFORE_CLOSING = 30;

    /**
     * Parse time string "HH:MM" to minutes since midnight
     */
    function timeToMinutes(timeStr) {
        const parts = timeStr.split(':');
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }

    /**
     * Convert minutes since midnight to "HH:MM" format
     */
    function minutesToTime(minutes) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hours.toString().padStart(2, '0') + ':' + mins.toString().padStart(2, '0');
    }

    /**
     * Get today's date in YYYY-MM-DD format
     */
    function getTodayDateString() {
        const now = new Date();
        return now.toISOString().split('T')[0];
    }

    /**
     * Get current time in minutes since midnight (Swedish timezone)
     */
    function getCurrentMinutes() {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
    }

    /**
     * Get opening hours for a specific date
     * Checks special hours first, then regular hours
     */
    function getHoursForDate(dateStr, openingData) {
        if (!openingData) return null;

        // Check special hours first
        if (openingData.specialHours && openingData.specialHours.length > 0) {
            const special = openingData.specialHours.find(function (s) {
                return s.date === dateStr;
            });
            if (special) {
                return special.isClosed ? null : {
                    open: timeToMinutes(special.open),
                    close: timeToMinutes(special.close),
                    isClosed: special.isClosed
                };
            }
        }

        // Fall back to regular hours
        const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone issues
        const jsDayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const arrayIndex = jsDayToArrayIndex(jsDayOfWeek); // Convert to metafield array index (0 = Monday)
        const dayName = WEEKDAY_NAMES_SV[arrayIndex]; // Swedish weekday name

        if (openingData.hours && openingData.hours.length > 0) {
            // Try to find by day name first (metafield format)
            let dayHours = openingData.hours.find(function(h) {
                return h.day && h.day.toLowerCase() === dayName;
            });

            // Fallback to array index if no day property
            if (!dayHours && openingData.hours[arrayIndex]) {
                dayHours = openingData.hours[arrayIndex];
            }

            if (dayHours) {
                return dayHours.isClosed ? null : {
                    open: timeToMinutes(dayHours.open),
                    close: timeToMinutes(dayHours.close),
                    isClosed: dayHours.isClosed
                };
            }
        }

        return null;
    }

    /**
     * Find the next opening day starting from a given date
     * Returns { date: 'YYYY-MM-DD', hours: { open, close }, dayName: 'monday' }
     */
    function findNextOpeningDay(startDateStr, openingData, maxDaysToCheck) {
        maxDaysToCheck = maxDaysToCheck || 14; // Check up to 2 weeks ahead

        const startDate = new Date(startDateStr + 'T12:00:00');

        for (let i = 1; i <= maxDaysToCheck; i++) {
            const checkDate = new Date(startDate);
            checkDate.setDate(checkDate.getDate() + i);
            const checkDateStr = checkDate.toISOString().split('T')[0];

            const hours = getHoursForDate(checkDateStr, openingData);
            if (hours && !hours.isClosed) {
                const dayOfWeek = checkDate.getDay();
                return {
                    date: checkDateStr,
                    hours: hours,
                    dayName: WEEKDAY_NAMES[dayOfWeek]
                };
            }
        }

        return null; // No opening day found
    }

    /**
     * Calculate pickup time and message
     * Returns { message: string, isOpen: boolean }
     */
    function calculatePickupTime(openingData, openText, closedText) {
        if (!openingData || !openingData.hours) {
            return { message: '', isOpen: false };
        }

        const todayStr = getTodayDateString();
        const currentMinutes = getCurrentMinutes();
        const todayHours = getHoursForDate(todayStr, openingData);

        // Check if store is open today
        if (todayHours && !todayHours.isClosed) {
            const minutesUntilClose = todayHours.close - currentMinutes;

            // Store is open and there's enough time for pickup
            if (currentMinutes >= todayHours.open && minutesUntilClose >= MIN_MINUTES_BEFORE_CLOSING) {
                const closeTime = minutesToTime(todayHours.close);
                let message = openText || 'Kan hämtas idag före [time]';
                message = message.replace('[time]', closeTime);
                message = message.replace('[day]', 'idag');
                return { message: message, isOpen: true };
            }

            // Store opens later today (before opening time)
            if (currentMinutes < todayHours.open) {
                const pickupTime = minutesToTime(todayHours.open + 30);
                let message = closedText || 'Kan hämtas [day] kl [time]';
                message = message.replace('[time]', pickupTime);
                message = message.replace('[day]', 'idag');
                return { message: message, isOpen: false };
            }
        }

        // Store is closed or closing soon - find next opening day
        const nextOpening = findNextOpeningDay(todayStr, openingData);

        if (nextOpening) {
            // Add 30 minutes to opening time
            const pickupTime = minutesToTime(nextOpening.hours.open + 30);
            let message = closedText || 'Kan hämtas [day] kl [time]';
            message = message.replace('[time]', pickupTime);
            // Use "idag" if next opening is today (edge case)
            const dayLabel = nextOpening.date === todayStr ? 'idag' : nextOpening.dayName;
            message = message.replace('[day]', dayLabel);
            return { message: message, isOpen: false };
        }

        // No opening day found
        return { message: '', isOpen: false };
    }

    /**
     * Update pickup time display
     */
    function updatePickupTimeDisplay(storeAvailabilityEl, openingData, settings) {
        if (!storeAvailabilityEl || !settings.enableDynamicPickupTime) return;

        const result = calculatePickupTime(
            openingData,
            settings.pickupOpenText,
            settings.pickupClosedText
        );

        if (result.message) {
            const messageSpan = storeAvailabilityEl.querySelector('span');
            if (messageSpan) {
                messageSpan.textContent = result.message;
            }

            // Update icon color based on status
            const iconClass = result.isOpen ? 'store-availability--in-stock' : 'store-availability--low-stock';
            storeAvailabilityEl.className = 'product__store-availability no-js-hidden ' + iconClass;
            storeAvailabilityEl.style.display = 'flex';
        } else {
            storeAvailabilityEl.style.display = 'none';
        }
    }

    // Initialize on page load
    if (typeof window.PUB_SUB_EVENTS !== 'undefined') {
        // Update on variant change
        subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
            const variant = event.data.variant;
            if (!variant) return;

            const storeAvailabilityEl = document.getElementById('StoreAvailability-' + event.data.sectionId);
            if (!storeAvailabilityEl) return;

            // Check if dynamic pickup time is enabled
            if (storeAvailabilityEl.dataset.enableDynamicPickupTime !== 'true') return;

            // Get inventory quantity from variant (now included in event) or from element data attribute
            const inventoryQuantity = variant.inventory_quantity !== null && variant.inventory_quantity !== undefined
                ? variant.inventory_quantity
                : parseInt(storeAvailabilityEl.dataset.inventoryQuantity) || 0;

            // Only update if product is in stock
            if (inventoryQuantity > 0) {
                const openingData = window.opening_hours_data; // Set in theme.liquid

                // Check if opening hours data is available
                if (!openingData || !openingData.hours) {
                    // Fallback to static in-stock message if no opening hours configured
                    const inStockText = storeAvailabilityEl.dataset.inStockText || 'Finns i butik - kan hämtas samma dag';
                    const messageSpan = storeAvailabilityEl.querySelector('span');
                    if (messageSpan) {
                        messageSpan.textContent = inStockText;
                    }
                    storeAvailabilityEl.className = 'product__store-availability no-js-hidden store-availability--in-stock';
                    storeAvailabilityEl.style.display = 'flex';
                    return;
                }

                const settings = {
                    enableDynamicPickupTime: true,
                    pickupOpenText: storeAvailabilityEl.dataset.pickupOpenText,
                    pickupClosedText: storeAvailabilityEl.dataset.pickupClosedText
                };
                updatePickupTimeDisplay(storeAvailabilityEl, openingData, settings);
            }
        });
    }

    // Export for use in Liquid template on initial page load
    window.PickupTimeCalculator = {
        calculate: calculatePickupTime,
        update: updatePickupTimeDisplay
    };
})();
